import * as React from "react";
import { useState, useEffect } from "react";
import { Dialog, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { emit } from "react-native-react-bridge/lib/web";
import { holdCartList, updatecartStatus, dataRetriveWhenPageRefresh } from "../../../../config/Services";
import { getStoreCurrencySymbol } from "../../../../utils/currency";
import "./HoldOrderPage.css";
import { getMobilePos } from "./redux/Reducers/paginationReducer";
import { useSnackbar } from "../hooks/SnackbarProvider";
import { setHoldOrdersCount } from "./redux/Actions/holdOrdersCountActions";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import ShoppingBasketOutlinedIcon from "@mui/icons-material/ShoppingBasketOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const TABS = ["All", "Dine In", "Delivery", "Takeaway"];

const BADGE_CONFIG = {
  DINE_IN:     { label: "Dine In",  cls: "hold-badge-dine-in" },
  QSR_DINE_IN: { label: "Dine In",  cls: "hold-badge-dine-in" },
  DELIVERY:    { label: "Delivery", cls: "hold-badge-delivery" },
  TAKE_AWAY:   { label: "Takeaway", cls: "hold-badge-takeaway" },
};

const tabToSubOrderType = {
  "Dine In":  "DINE_IN",
  "Delivery": "DELIVERY",
  "Takeaway": "TAKE_AWAY",
};

function toIST(dateStr) {
  if (!dateStr) return "";
  try {
    const normalized = dateStr.endsWith("Z") || dateStr.includes("+") ? dateStr : dateStr + "Z";
    return new Date(normalized).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "";
  }
}

function getProductSummary(cartProducts = []) {
  return cartProducts
    .slice(0, 3)
    .map((p) => {
      const qty = p.quantity > 1 ? `${p.quantity}x ` : "";
      return `${qty}${p.variantName || p.productName}`;
    })
    .join(", ");
}

const HoldOrderPage = () => {
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const dispatch = useDispatch();
  const isMobilePos = useSelector(getMobilePos);

  const [open, setOpen] = useState(true);
  const [loader, setLoader] = useState(false);
  const [actionLoader, setActionLoader] = useState(null); // cartId being actioned
  const [results, setResults] = useState([]);
  const [activeTab, setActiveTab] = useState("All");
  const [search, setSearch] = useState("");

  const appConfig = JSON.parse(localStorage.getItem("appConfig") || "{}");
  const holdCartVisibility = appConfig?.enabledFeatures?.HOLD_CART_VISIBLE;
  const terminalName = localStorage.getItem("licenseName");
  const retailType = localStorage.getItem("retailType");
  const retailUserDetails = JSON.parse(localStorage.getItem("retailUserDetails") || "{}");
  const firstName = retailUserDetails?.firstName;
  const uidx = retailUserDetails?.uidx;
  const locId = localStorage.getItem("locId");

  const handleClose = () => {
    setOpen(false);
  };

  const fetchHoldOrders = () => {
    setLoader(true);
    const payload = {
      locationId: [locId],
      cartStatus: ["HOLD"],
      ...(!holdCartVisibility ? { terminalName: [terminalName] } : {}),
    };
    holdCartList(payload)
      .then((res) => {
        const data = res?.data?.data?.results || [];
        setResults(data);
        dispatch(setHoldOrdersCount(data.length));
        setLoader(false);
      })
      .catch((err) => {
        showSnackbar(err?.response?.data?.message || err?.message, "error");
        setLoader(false);
      });
  };

  useEffect(() => {
    fetchHoldOrders();
  }, []);

  const handleAction = async (cartId, mobileNo, userName, type) => {
    try {
      setActionLoader({ cartId, type });
      const payload = {
        cartId,
        cartStatus: type === "delete" ? "USER_HOLD_DELETED" : "UN_HOLD",
        comments: type === "delete" ? `Deleted by ${firstName}` : `Unhold by ${firstName}`,
        updatedByUser: uidx || null,
        updatedByName: firstName || null,
      };
      const res = await updatecartStatus(payload);
      if (type === "unhold") {
        const resolvedCartId = res?.data?.data?.cartId || cartId;
        const existingCustomer = JSON.parse(localStorage.getItem("customer_details") || "{}");
        const updatedCustomer = {
          ...existingCustomer,
          name: userName || null,
          mobileNumber: mobileNo || null,
          uidx: res?.data?.data?.userId,
        };
        localStorage.setItem("customer_details", JSON.stringify(updatedCustomer));
        localStorage.setItem("CartId", resolvedCartId);
        localStorage.removeItem("retrieveHoldCart");

        isMobilePos &&
          emit({
            type: "localData",
            data: { CartId: resolvedCartId, customer_details: updatedCustomer },
          });
        showSnackbar("Hold cart retrieved successfully", "success");
        if (retailType === "RESTAURANT") {
          const cartRes = await dataRetriveWhenPageRefresh(resolvedCartId).catch(() => null);
          const cartData = cartRes?.data?.data?.data || null;
          const subOrderType = cartData?.subOrderType;
          if (subOrderType === "DELIVERY" || subOrderType === "TAKE_AWAY") {
            localStorage.setItem("fineDineSaleMode", subOrderType);
          } else {
            localStorage.removeItem("fineDineSaleMode");
          }
          navigate("/restaurant/cart", { state: { holdCartData: cartData, holdCustomer: updatedCustomer } });
        } else {
          navigate("/grocery/cart");
        }
      } else {
        showSnackbar("Hold cart discarded successfully", "success");
        fetchHoldOrders();
      }
    } catch (err) {
      showSnackbar(err?.response?.data?.message || err?.message, "error");
    } finally {
      setActionLoader(null);
    }
  };

  const filtered = results.filter((r) => {
    if (activeTab !== "All") {
      const expected = tabToSubOrderType[activeTab];
      const isDineInTab = expected === "DINE_IN";
      const cartIsDineIn = r.subOrderType === "DINE_IN" || r.subOrderType === "QSR_DINE_IN";
      if (isDineInTab ? !cartIsDineIn : r.subOrderType !== expected) return false;
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      return (
        r.cartId?.toLowerCase().includes(q) ||
        r.tableNumber?.toString().includes(q) ||
        r.userName?.toLowerCase().includes(q) ||
        r.mobileNo?.includes(q) ||
        r.createdByName?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ className: "hold-dialog-paper" }}
      sx={{ zIndex: 2000 }}
    >
      {/* Header */}
      <div className="hold-dialog-header">
        <p className="hold-dialog-title">Hold Table ({results.length})</p>
        <p className="hold-dialog-subtitle">Orders currently on hold</p>
        <button className="hold-dialog-close" onClick={handleClose}>
          <CloseIcon sx={{ fontSize: "1.2rem" }} />
        </button>
      </div>

      {/* Toolbar */}
      <div className="hold-dialog-toolbar">
        <div className="hold-tabs">
          {TABS.map((tab) => (
            <button
              key={tab}
              className={`hold-tab-btn ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>
        <div className="hold-search-wrap">
          <SearchIcon sx={{ fontSize: "1rem", color: "#9ca3af" }} />
          <input
            className="hold-search-input"
            placeholder="Search KOT, table no."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Body */}
      <div className="hold-dialog-body">
        <div className="hold-cards-grid">
          {loader ? (
            <div className="hold-loader-wrap">
              <CircularProgress sx={{ color: "#2563eb" }} />
            </div>
          ) : filtered.length === 0 ? (
            <div className="hold-empty">No hold orders found</div>
          ) : (
            filtered.map((r) => {
              const badge = BADGE_CONFIG[r.subOrderType] || { label: r.subOrderType, cls: "hold-badge-takeaway" };
              const productSummary = getProductSummary(r.cartProducts || []);
              const itemCount = r.billing?.quantity || (r.cartProducts?.length ?? 0);
              const totalValue = r.billing?.totalCartValue;
              const isDiscarding = actionLoader?.cartId === r.cartId && actionLoader?.type === "delete";
              const isRetrieving = actionLoader?.cartId === r.cartId && actionLoader?.type === "unhold";
              const isActioning = isDiscarding || isRetrieving;

              return (
                <div key={r.cartId} className="hold-card">
                  {/* Top row: avatar + customer + badge */}
                  <div className="hold-card-top">
                    <div className="hold-card-customer">
                      <div className="hold-card-avatar">
                        <PersonOutlineIcon sx={{ fontSize: "1.2rem" }} />
                      </div>
                      <div>
                        <p className="hold-card-name">{r.userName || r.createdByName || "Guest"}</p>
                        <p className="hold-card-phone">{r.mobileNo || r.tableNumber ? `Table ${r.tableNumber}` : r.cartId}</p>
                      </div>
                    </div>
                    <span className={`hold-card-badge ${badge.cls}`}>{badge.label}</span>
                  </div>

                  {/* Meta: time + items */}
                  <div className="hold-card-meta">
                    <span className="hold-card-meta-item">
                      <AccessTimeOutlinedIcon sx={{ fontSize: "0.9rem" }} />
                      {toIST(r.createdDate)}
                    </span>
                    <span className="hold-card-meta-item">
                      <ShoppingBasketOutlinedIcon sx={{ fontSize: "0.9rem" }} />
                      {itemCount} Items
                    </span>
                  </div>

                  {/* Product names */}
                  {productSummary && (
                    <p className="hold-card-products">{productSummary}</p>
                  )}

                  {/* Total */}
                  <div className="hold-card-total">
                    <span className="hold-card-total-label">Total Value</span>
                    <span className="hold-card-total-value">
                      {getStoreCurrencySymbol()}{Number(totalValue).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="hold-card-actions">
                    <button
                      className="hold-btn-discard"
                      disabled={isActioning}
                      onClick={() => handleAction(r.cartId, r.mobileNo, r.userName, "delete")}
                    >
                      {isDiscarding ? (
                        <CircularProgress size={14} sx={{ color: "#ef4444" }} />
                      ) : (
                        <>
                          Discard <DeleteOutlineIcon sx={{ fontSize: "1rem" }} />
                        </>
                      )}
                    </button>
                    <button
                      className="hold-btn-retrieve"
                      disabled={isActioning}
                      onClick={() => handleAction(r.cartId, r.mobileNo, r.userName, "unhold")}
                    >
                      {isRetrieving ? (
                        <CircularProgress size={14} sx={{ color: "#fff" }} />
                      ) : (
                        "Retrieve to Cart"
                      )}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default HoldOrderPage;
